/**
 * @fileoverview Reactive hook for department authentication setup workflows.
 * @module system-web/hooks/useDepartmentAuthSetup
 *
 * Responsibilities:
 * - Subscribe to the pure Dexie authentication-state observable.
 * - Derive the seven-stage workflow and expose connectivity/action state.
 *
 * Dependencies:
 * - Department authentication service and React subscription/state hooks.
 *
 * Architecture:
 * - Reads setup state from Dexie and delegates server-backed actions to the service.
 *
 * Notes:
 * - Secret values are never exposed in the state stream.
 *
 * @hook
 * Purpose: Provides tenant authentication setup state and actions to components.
 * Returns: Current setup state, derived stage, connectivity, pending, and actions.
 * Side Effects: Subscribes to local setup state and invokes service mutations.
 * Offline Behavior: Reads cached state offline; server-backed actions require connectivity.
 * @localFirst
 * @errorHandling Propagates service failures without exposing secret values.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Observable } from 'dexie';
import {
  activateLockedAuthMethod,
  createInteractiveTestSession,
  createItHandoffLink,
  deriveAuthSetupStage,
  generateItSetupPacket,
  getItSetupPacket,
  getDepartmentAuthState,
  runServerValidationAndGraphSync,
  revertToLocalAuthMethod,
  saveAuthSetupDraft,
  submitItCredentialsAdmin,
  type DepartmentAuthDraftPatch,
  type DepartmentAuthState,
} from '../services/departmentAuthService';
import { watchDepartmentAuthState } from '../services/departmentAuthService';

export function useDepartmentAuthSetup(tenantId: string) {
  const [state, setState] = useState<DepartmentAuthState | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);
  const [isOnline, setIsOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);

  useEffect(() => {
    let active = true;
    const observable: Observable<DepartmentAuthState> = watchDepartmentAuthState(tenantId);
    const subscription = observable.subscribe({
      next: (value) => { if (active) setState(value); },
      error: (value) => { if (active) setError(value instanceof Error ? value : new Error('Unable to read authentication state')); },
    });
    void getDepartmentAuthState(tenantId).then((value) => { if (active) setState(value); }).catch((value) => {
      if (active) setError(value instanceof Error ? value : new Error('Unable to read authentication state'));
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, [tenantId]);

  useEffect(() => {
    const online = () => setIsOnline(true);
    const offline = () => setIsOnline(false);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline); };
  }, []);

  const run = useCallback(async <T,>(action: () => Promise<T>): Promise<T> => {
    setPending(true);
    setError(null);
    try {
      return await action();
    } catch (value) {
      const next = value instanceof Error ? value : new Error('Authentication setup action failed');
      setError(next);
      throw next;
    } finally {
      setPending(false);
    }
  }, []);

  return useMemo(() => ({
    state,
    stage: state ? deriveAuthSetupStage(state) : 'choose',
    isOnline,
    pending,
    saving: pending,
    error,
    saveDraft: (patch: DepartmentAuthDraftPatch) => run(() => saveAuthSetupDraft(tenantId, patch)),
    generatePacket: () => run(() => generateItSetupPacket(tenantId)),
    getPacket: (version: number) => run(() => getItSetupPacket(tenantId, version)),
    createHandoffLink: (sendEmail: boolean) => run(() => createItHandoffLink(tenantId, { sendEmail })),
    submitCredentials: (credentials: Record<string, unknown>) => run(() => submitItCredentialsAdmin(tenantId, credentials)),
    validateServer: () => run(() => runServerValidationAndGraphSync(tenantId)),
    createTestSession: () => run(() => createInteractiveTestSession(tenantId)),
    activate: () => run(() => activateLockedAuthMethod(tenantId)),
    revertToLocal: (reason: string) => run(() => revertToLocalAuthMethod(tenantId, reason)),
  }), [error, isOnline, pending, run, state, tenantId]);
}
